import { Injectable, effect, inject } from '@angular/core';
import { environment } from '@env';
import { CookieConsentService } from '../cookies';

type PostHogClient = (typeof import('posthog-js'))['default'];

@Injectable({
  providedIn: 'root',
})
export class PostHogService {
  private readonly cookieConsent = inject(CookieConsentService);
  #posthog: PostHogClient | null = null;
  #posthogImportPromise: Promise<PostHogClient> | null = null;
  #initialized = false;
  #initializing = false;

  constructor() {
    // Only initialize PostHog when analytics consent is given
    effect(() => {
      const hasConsent = this.cookieConsent.hasConsent();

      if (hasConsent === true && !this.#initialized) {
        void this.initializePostHog();
      } else if (hasConsent === false && this.#initialized) {
        this.shutdownPostHog();
      }
    });
  }

  private loadPostHog(): Promise<PostHogClient> {
    if (this.#posthogImportPromise) {
      return this.#posthogImportPromise;
    }

    this.#posthogImportPromise = import('posthog-js').then(
      (module) => module.default,
    );

    return this.#posthogImportPromise;
  }

  private async initializePostHog(): Promise<void> {
    if (this.#initialized || this.#initializing) return;

    this.#initializing = true;
    try {
      const posthog = await this.loadPostHog();

      if (this.cookieConsent.hasConsent() !== true) {
        return;
      }

      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        disable_session_recording: false,
        respect_dnt: true,
        persistence: 'localStorage+cookie',
        secure_cookie: environment.production,
        cross_subdomain_cookie: true,
        enable_recording_console_log: environment.envName === 'production',
        loaded: (posthog) => {
          // Set environment info for all events
          posthog.register({
            environment:
              environment.envName ??
              (environment.production ? 'production' : 'development'),
            app_version: '0.0.1',
            app_name: 'zynday',
            source: 'frontend',
          });
        },
      });

      this.#posthog = posthog;
      this.#initialized = true;
    } catch (error) {
    } finally {
      this.#initializing = false;
    }
  }

  private shutdownPostHog(): void {
    if (!this.#initialized) return;

    try {
      if (this.#posthog) {
        this.#posthog.reset();
        this.#posthog.opt_out_capturing();
      }
      this.#initialized = false;
      this.#posthog = null;
    } catch (error) {}
  }

  /**
   * Identify a user (only if analytics consent is given)
   */
  identify(userId: string, properties?: Record<string, unknown>): void {
    if (!this.cookieConsent.hasConsent()) {
      return;
    }

    if (!this.#initialized || !this.#posthog) {
      void this.initializePostHog();
      return;
    }

    try {
      this.#posthog.identify(userId, properties);
    } catch (error) {}
  }

  /**
   * Track an event (only if analytics consent is given)
   */
  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.cookieConsent.hasConsent()) {
      return;
    }

    if (!this.#initialized || !this.#posthog) {
      void this.initializePostHog();
      return;
    }

    try {
      this.#posthog.capture(eventName, properties);
    } catch (error) {}
  }

  /**
   * Reset user data (for logout)
   */
  reset(): void {
    try {
      if (this.#posthog) {
        this.#posthog.reset();
      }
    } catch (error) {}
  }

  /**
   * Get PostHog instance for advanced usage
   */
  getPostHog(): PostHogClient | null {
    return this.#posthog;
  }

  isFeatureEnabled(feature: string) {
    if (!this.#posthog || !this.#initialized) {
      return false;
    }

    return this.#posthog.isFeatureEnabled(feature);
  }
}
